import { useState } from "react";
import {
  Play,
  BookOpen,
  HelpCircle,
  CheckCircle,
  ChevronRight,
  Building2,
  Users,
  FileText,
  DollarSign,
  Briefcase,
  Building,
  Wrench,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const HelpCenter = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  const gettingStartedSteps = [
    {
      step: 1,
      title: "Create Clients",
      description: "Start by adding your clients (companies or individuals who own properties)",
      icon: Briefcase,
      link: "/clients",
      color: "bg-blue-100 text-blue-700"
    },
    {
      step: 2,
      title: "Add LLCs/Owners",
      description: "Create LLCs or personal ownership entities under each client",
      icon: Building,
      link: "/owners",
      color: "bg-purple-100 text-purple-700"
    },
    {
      step: 3,
      title: "Add Properties",
      description: "Add properties and assign them to the appropriate LLC/Owner",
      icon: Building2,
      link: "/properties",
      color: "bg-green-100 text-green-700"
    },
    {
      step: 4,
      title: "Add Tenants",
      description: "Add tenants to each property (bulk import available)",
      icon: Users,
      link: "/tenants",
      color: "bg-amber-100 text-amber-700"
    },
    {
      step: 5,
      title: "Create Leases",
      description: "Create lease agreements linking tenants to properties",
      icon: FileText,
      link: "/leases",
      color: "bg-red-100 text-red-700"
    },
    {
      step: 6,
      title: "Track Payments & Generate Invoices",
      description: "Record rent payments and generate invoices",
      icon: DollarSign,
      link: "/rent-payments",
      color: "bg-emerald-100 text-emerald-700"
    }
  ];

  const faqItems = [
    {
      question: "How do I organize properties by client?",
      answer: "Start by creating a Client (company or individual), then create LLCs/Owners under that client, and finally add properties under each LLC. This creates a clear hierarchy: Client → LLC → Properties → Tenants."
    },
    {
      question: "How do I generate invoices?",
      answer: "Go to Invoices page and click 'Generate Monthly Invoices'. The system will create invoices for all active leases based on rent amounts and any late fees. You can review, approve, and send them via email."
    },
    {
      question: "How do I import multiple tenants at once?",
      answer: "Go to Tenants page, click 'Bulk Import', download the CSV template, fill it with tenant data, and upload. You can also use the AI-powered image import to extract tenant data from images."
    },
    {
      question: "How are late fees calculated?",
      answer: "Late fees are based on each lease's settings. By default, a late fee applies after the 7th of the month (5 days + 2 grace days). You can customize this per lease."
    },
    {
      question: "How do I track work orders?",
      answer: "Go to Work Orders, create a new work order, assign it to a property and optionally a tenant. You can set priority levels and track status from Open to Completed."
    },
    {
      question: "How do I view financial reports?",
      answer: "Go to Financial Reports to see P&L statements and Balance Sheets. You can filter by date range, LLC, or property. Reports are generated at the LLC level with client-level summaries available."
    },
    {
      question: "Who can access what in the system?",
      answer: "Admin has full access. Managers can create/edit most data. Viewers have read-only access. Coming soon: Assign users to specific clients for limited portfolio access."
    }
  ];

  const videoTutorials = [
    {
      id: "getting-started",
      title: "Getting Started - Platform Overview",
      duration: "5 min",
      description: "Learn the basics of navigating the platform and setting up your first client"
    },
    {
      id: "client-setup",
      title: "Setting Up Clients & LLCs",
      duration: "3 min",
      description: "How to create the client hierarchy with multiple LLCs"
    },
    {
      id: "property-management",
      title: "Managing Properties & Tenants",
      duration: "4 min",
      description: "Add properties, import tenants, and create leases"
    },
    {
      id: "invoicing",
      title: "Invoicing & Payments",
      duration: "4 min",
      description: "Generate invoices, track payments, and handle late fees"
    },
    {
      id: "reports",
      title: "Financial Reports",
      duration: "3 min",
      description: "Understanding P&L statements and Balance Sheets"
    }
  ];

  return (
    <div className="animate-fade-in" data-testid="help-center-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Help Center</h1>
          <p className="text-slate-500 mt-1">Learn how to use AV Realty Management</p>
        </div>
      </div>

      {/* Quick Start Guide */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-green-600" />
            Quick Start Guide
          </CardTitle>
          <CardDescription>
            Follow these steps to set up your property management system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gettingStartedSteps.map((item) => (
              <a
                key={item.step}
                href={item.link}
                className="block p-4 border rounded-xl hover:shadow-md transition-all hover:border-green-300 group"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">Step {item.step}</Badge>
                    </div>
                    <h3 className="font-semibold mt-1 group-hover:text-green-700">{item.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-green-600 mt-2" />
                </div>
              </a>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Video Tutorials */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-red-500" />
              Video Tutorials
            </CardTitle>
            <CardDescription>
              Watch step-by-step video guides
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {videoTutorials.map((video) => (
                <div
                  key={video.id}
                  className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => setActiveVideo(video.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      <Play className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">{video.title}</h4>
                      <p className="text-sm text-slate-500">{video.description}</p>
                    </div>
                    <Badge variant="outline">{video.duration}</Badge>
                  </div>
                </div>
              ))}
            </div>

            {/* Placeholder for video - will be replaced with actual video embed */}
            <div className="mt-6 p-8 bg-slate-100 rounded-xl text-center">
              <Play className="w-16 h-16 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600 font-medium">Video tutorials coming soon!</p>
              <p className="text-sm text-slate-500 mt-2">
                We're creating helpful video guides to walk you through every feature.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-500" />
              Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </div>

      {/* Data Structure Overview */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Understanding the Data Structure</CardTitle>
          <CardDescription>
            How your property portfolio is organized
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 rounded-xl p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold">Client</p>
                  <p className="text-xs text-slate-500">Company or Individual</p>
                </div>
              </div>
              
              <ChevronRight className="w-6 h-6 text-slate-300 hidden md:block" />
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold">LLC / Owner</p>
                  <p className="text-xs text-slate-500">Multiple per client</p>
                </div>
              </div>
              
              <ChevronRight className="w-6 h-6 text-slate-300 hidden md:block" />
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold">Property</p>
                  <p className="text-xs text-slate-500">Multiple per LLC</p>
                </div>
              </div>
              
              <ChevronRight className="w-6 h-6 text-slate-300 hidden md:block" />
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold">Tenants</p>
                  <p className="text-xs text-slate-500">Per property/unit</p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-slate-600">
                <strong>Example:</strong> "AV Group" (Client) → "AV Properties LLC" (LLC) → "123 Main St" (Property) → "John Smith, Unit 1A" (Tenant)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="mt-8 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <CardContent className="py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold">Need more help?</h3>
              <p className="text-green-100 mt-1">
                Contact your administrator or reach out to AV Realty Management support
              </p>
            </div>
            <Button variant="secondary" className="bg-white text-green-700 hover:bg-green-50">
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenter;
